/**
 * Everything about a panel that is teleported out of the component that declared it.
 *
 * Menus and popovers have to leave: `position: fixed` resolves against the nearest ancestor
 * carrying a transform rather than the viewport, so inside a draggable panel (which always
 * holds a translate) the coordinates land somewhere else entirely, and whatever has overflow
 * set then clips what is left. `x-teleport` moves a CLONE somewhere harmless.
 *
 * Where "harmless" is depends on what the trigger sits inside, and both wrong answers are
 * silent — the thing opens onto nothing, with no error anywhere:
 *
 *  - <body> is invisible underneath a modal. A dialog opened with showModal() renders in the
 *    browser's TOP LAYER, above every z-index the document can offer.
 *  - the component's own tree is where Livewire's morph finds a child the server never sent,
 *    and deletes it.
 *
 * So {@see hostFor} picks the host, {@see reattach} recovers a panel that was deleted anyway,
 * and app.js tells morph to spare teleported clones in the first place. Verified against the
 * real Alpine and the real morph config — reasoning from their source produced three confident
 * wrong answers first. @see ./teleported-panel.test.js
 */

/** The attribute Alpine stamps on every teleported clone. */
export const TELEPORT_CLONE = 'data-teleport-target';

/**
 * Where a teleported panel should LIVE — a different question from where it is drawn.
 *
 * In order:
 *
 *  - Inside an OPEN DIALOG → that dialog. The top layer sits above every z-index in the
 *    document, so a body-parked panel renders underneath it and simply never appears.
 *  - Inside a FLOATING PANEL → <body>. The panel replaced the dialogs on those surfaces, so
 *    there is no top layer to out-stack. (A menu needing `wire:` directives could not live
 *    there — Livewire resolves them by walking up for a wire:id — but these filter client-side,
 *    and a future one that needs the server should say so rather than inherit this default.)
 *  - Inside a Livewire component, elsewhere → the component root, so `wire:model` searches keep
 *    resolving. Only for a panel that CONTAINS a `wire:` directive: Livewire resolves those by
 *    walking up for a wire:id, and would find none in <body>. A panel that only reads and
 *    filters — a help popover — should pass `needsWireScope: false` and stay in <body>, which
 *    is the one place a morph never looks and no transformed ancestor can clip.
 *  - Otherwise → <body>.
 */
export function hostFor(trigger, { needsWireScope = true } = {}) {
    if (! trigger) {
        return document.body;
    }

    /*
     * The dialog rule outranks the panel rule: a floating panel rendered inside an open dialog
     * is IN the top layer with it, and the top layer sits above any z-index <body> can offer.
     */
    const dialog = trigger.closest('dialog[open]');

    if (dialog) {
        return dialog;
    }

    if (trigger.closest('[data-floating-panel]')) {
        return document.body;
    }

    if (! needsWireScope) {
        return document.body;
    }

    return trigger.closest('[wire\\:id]') || document.body;
}

/**
 * Is this the browser-side clone of a teleported element?
 *
 * Used to tell Livewire's morph to leave it alone. Written against a DOM node of any type,
 * because morph offers text and comment nodes for removal too.
 */
export function isTeleportedClone(node) {
    return node?.nodeType === 1 && node.hasAttribute?.(TELEPORT_CLONE);
}

/**
 * Put a panel back where it belongs, before measuring it.
 *
 * Covers the ordinary case — it is parked somewhere else, because the right host changes when a
 * modal opens — and the damaged one, where a morph removed it and it has no parent at all.
 * Re-attaching beats waiting for Alpine to build another clone: the element is intact, still
 * carries the component's scope, and is still what the ref points at. It is only orphaned.
 *
 * @returns {boolean} whether the panel is now in the document and can be measured
 */
export function reattach(panel, host) {
    if (! panel || ! host) {
        return false;
    }

    if (panel.parentElement !== host) {
        host.appendChild(panel);
    }

    return panel.isConnected;
}

/**
 * Place a panel at viewport coordinates, by MEASUREMENT rather than by assumption.
 *
 * `position: fixed` resolves against the viewport — unless an ancestor has a transform, in
 * which case it resolves against THAT, and Flux animates its modals with a scale. Rather than
 * guess which containing block we are in, pin the panel at 0,0, ask the browser where that
 * landed, and offset from there. Comes out the same in both cases and needs to know nothing
 * about the modal.
 */
export function pinToViewport(panel, { top, left, minWidth = null, zIndex = null }) {
    if (! panel) {
        return;
    }

    panel.style.position = 'fixed';
    panel.style.top = '0px';
    panel.style.left = '0px';

    const origin = panel.getBoundingClientRect();

    panel.style.top = `${Math.round(top - origin.top)}px`;
    panel.style.left = `${Math.round(left - origin.left)}px`;

    if (minWidth !== null) {
        panel.style.minWidth = `${Math.round(minWidth)}px`;
    }

    if (zIndex !== null) {
        panel.style.zIndex = String(zIndex);
    }
}
